import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Dropdown, Form, Modal, ProgressBar, Spinner, Table } from 'react-bootstrap';
import { ArrowClockwise, Plus } from 'react-bootstrap-icons';

import { validateAnnouncementDraft, type AnnouncementDraft } from '../../../../lib/notifications/announcements';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { reasonOf } from '@/components/tools/clientSelectionUtils';
import { AnnouncementPreview } from '@/components/announcements/AnnouncementPreview';
import { WorkspaceRunTable } from '@/components/announcements/WorkspaceRunTable';
import { AnnouncementForm } from '@/components/announcements/AnnouncementForm';
import {
  emptyFormValues,
  toDateInput,
  toDraft,
  type AnnouncementFormValues,
} from '@/components/announcements/announcementFormValues';
import {
  checkReadinessOf,
  describeTargets,
  editAnnouncement,
  isReachableClient,
  newAnnouncementId,
  postAnnouncement,
  readFleetAnnouncements,
  restoreAnnouncement,
  revokeAnnouncement,
  type FleetAnnouncement,
  type FleetReadResult,
  type WorkspaceRunStatus,
  type WorkspaceReadiness,
  type WorkspaceRunSummary,
} from '@/services/announcementsService';
import { clientService } from '@/services/clientService';
import { listDeploymentGroups } from '@/services/deploymentService';
import { ToolPage } from '@/components/ui/ToolPage';
import { formatNzDate, timeAgo } from '@/utils/relativeTime';
import type { Client, DeploymentGroup } from '@/types';
import '@/components/announcements/announcements.css';

type View = { mode: 'list' } | { mode: 'compose' } | { mode: 'edit'; target: FleetAnnouncement };

type RunKind = 'post' | 'edit' | 'end' | 'revoke' | 'restore';
type RowAction = 'end' | 'revoke' | 'restore';

interface Run {
  kind: RunKind;
  title: string;
  /** Re-runs the same write on the named workspaces (Retry failed). */
  redo: (
    clients: Client[],
    onUpdate: (name: string, status: WorkspaceRunStatus) => void
  ) => Promise<WorkspaceRunSummary>;
  statuses: Record<string, WorkspaceRunStatus>;
  running: boolean;
  summary?: WorkspaceRunSummary;
}

const RUN_VERB: Record<RunKind, { doing: string; done: string }> = {
  post: { doing: 'Sending', done: 'Sent' },
  edit: { doing: 'Sending changes to', done: 'Sent changes to' },
  end: { doing: 'Ending the pop-up for', done: 'Ended the pop-up for' },
  revoke: { doing: 'Revoking', done: 'Revoked' },
  restore: { doing: 'Restoring', done: 'Restored' },
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const ROW_ACTION: Record<RowAction, { title: string; confirm: string; body: (a: FleetAnnouncement) => string }> = {
  end: {
    title: 'End the pop-up',
    confirm: 'End the pop-up',
    body: (a) =>
      `Nobody who has not seen "${a.title}" yet gets the pop-up from now on. It stays in the Announcements history.`,
  },
  revoke: {
    title: 'Revoke announcement',
    confirm: 'Revoke',
    body: (a) =>
      `"${a.title}" is hidden from everybody on ${plural(a.workspaces.length - a.revokedOn.length, 'workspace')}: ` +
      'no pop-up, and it leaves the Announcements history. It stays in this list, and Restore brings it back.',
  },
  restore: {
    title: 'Restore announcement',
    confirm: 'Restore',
    body: (a) =>
      `"${a.title}" is back in the Announcements history on ${plural(a.revokedOn.length, 'workspace')}. ` +
      'People who have not closed it yet get the pop-up if it is still inside its date.',
  },
};

/** A day, in New Zealand time: "13 Oct 2026". */
const formatDate = (epochMs: number): string => formatNzDate(epochMs);

export default function Announcements() {
  const [clients, setClients] = useState<Client[]>([]);
  const [groups, setGroups] = useState<DeploymentGroup[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fleet, setFleet] = useState<FleetReadResult | null>(null);
  const [reading, setReading] = useState(false);
  const [readProgress, setReadProgress] = useState({ checked: 0, total: 0 });

  const [view, setView] = useState<View>({ mode: 'list' });
  const [values, setValues] = useState<AnnouncementFormValues>(() => emptyFormValues());
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [pickedGroups, setPickedGroups] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ kind: RowAction; target: FleetAnnouncement } | null>(null);

  const [run, setRun] = useState<Run | null>(null);

  const clientsByName = useMemo(() => new Map(clients.map((c) => [c.name, c])), [clients]);
  const selectableClients = useMemo(() => clients.filter((c) => c.name !== 'platform-settings'), [clients]);

  const readFleet = useCallback(async (from: Client[]) => {
    setReading(true);
    setReadProgress({ checked: 0, total: from.filter(isReachableClient).length });
    try {
      const result = await readFleetAnnouncements(from, (checked, total) => setReadProgress({ checked, total }));
      setFleet(result);
    } catch (e) {
      setError(`The announcements could not be read. ${reasonOf(e)} Press Refresh to try again.`);
    } finally {
      setReading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allClients, allGroups] = await Promise.all([
          clientService.getAllClients(),
          listDeploymentGroups().catch(() => [] as DeploymentGroup[]),
        ]);
        if (cancelled) return;
        setClients(allClients);
        setGroups(allGroups);
        void readFleet(allClients);
      } catch (e) {
        if (!cancelled) setError(`The client list did not load. ${reasonOf(e)} Reload the page to try again.`);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readFleet]);

  // A changed input needs a fresh look at the preview before it can go out.
  useEffect(() => setAcknowledged(false), [values, selectedClientNames]);

  // The review step asks every chosen workspace whether it can show an
  // announcement yet, so an older one is flagged before anything is sent.
  const [readiness, setReadiness] = useState<Record<string, WorkspaceReadiness> | null>(null);
  useEffect(() => {
    if (!showConfirm) return undefined;
    let cancelled = false;
    setReadiness(null);
    void checkReadinessOf(targetClients.filter(isReachableClient)).then((answers) => {
      if (!cancelled) setReadiness(answers);
    });
    return () => {
      cancelled = true;
    };
    // Only when the dialog opens: the targets cannot change while it is open.
  }, [showConfirm]);
  const waitingNames = readiness
    ? Object.keys(readiness)
        .filter((n) => readiness[n] === 'outdated')
        .sort()
    : [];
  const uncheckedNames = readiness
    ? Object.keys(readiness)
        .filter((n) => readiness[n] === 'unknown')
        .sort()
    : [];

  const draft: AnnouncementDraft = useMemo(() => toDraft(values), [values]);
  const draftErrors = useMemo(() => validateAnnouncementDraft(draft, Date.now()), [draft]);

  const targetClients: Client[] = useMemo(() => {
    if (view.mode === 'edit') {
      return view.target.workspaces.map((w) => clientsByName.get(w.clientName)).filter((c): c is Client => !!c);
    }
    return selectedClientNames.map((n) => clientsByName.get(n)).filter((c): c is Client => !!c);
  }, [view, selectedClientNames, clientsByName]);
  const unreachable = targetClients.filter((c) => !isReachableClient(c));

  // ─── Composer ─────────────────────────────────────────────────────────────

  const startCompose = (): void => {
    setValues(emptyFormValues());
    setSelectedClientNames([]);
    setPickedGroups([]);
    setView({ mode: 'compose' });
  };

  const startEdit = (target: FleetAnnouncement): void => {
    setValues({
      title: target.title,
      body: target.body,
      linkLabel: target.linkLabel ?? '',
      linkUrl: target.linkUrl ?? '',
      audience: target.audience,
      showUntilDate: toDateInput(Math.max(target.showUntil, Date.now())),
    });
    setView({ mode: 'edit', target });
  };

  const addGroup = (groupName: string): void => {
    const group = groups.find((g) => g.groupName === groupName);
    if (!group) return;
    setSelectedClientNames((prev) => [...new Set([...prev, ...group.clients.filter((n) => clientsByName.has(n))])]);
    setPickedGroups((prev) => (prev.includes(groupName) ? prev : [...prev, groupName]));
  };

  const toggleClient = (name: string): void =>
    setSelectedClientNames((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const selectClients = (names: string[]): void => {
    setSelectedClientNames(names);
    if (names.length === 0) setPickedGroups([]);
  };

  // ─── Runs ─────────────────────────────────────────────────────────────────

  const startRun = async (kind: RunKind, title: string, runClients: Client[], redo: Run['redo']): Promise<void> => {
    const statuses: Record<string, WorkspaceRunStatus> = {};
    runClients.forEach((c) => (statuses[c.name] = { state: 'queued' }));
    setRun({ kind, title, redo, statuses, running: true });
    setView({ mode: 'list' });
    const onUpdate = (name: string, status: WorkspaceRunStatus): void =>
      setRun((prev) => (prev ? { ...prev, statuses: { ...prev.statuses, [name]: status } } : prev));
    const summary = await redo(runClients, onUpdate);
    setRun((prev) => (prev ? { ...prev, running: false, summary } : prev));
    void readFleet(clients);
  };

  const retryFailed = async (): Promise<void> => {
    if (!run?.summary) return;
    const failed = run.summary.failed.map((f) => clientsByName.get(f.clientName)).filter((c): c is Client => !!c);
    if (failed.length === 0) return;
    const onUpdate = (name: string, status: WorkspaceRunStatus): void =>
      setRun((prev) => (prev ? { ...prev, statuses: { ...prev.statuses, [name]: status } } : prev));
    setRun((prev) => (prev ? { ...prev, running: true } : prev));
    const retried = await run.redo(failed, onUpdate);
    setRun((prev) =>
      prev?.summary
        ? {
            ...prev,
            running: false,
            summary: {
              succeeded: [...prev.summary.succeeded, ...retried.succeeded].sort(),
              waiting: [...prev.summary.waiting, ...retried.waiting].sort(),
              failed: retried.failed,
            },
          }
        : prev
    );
    void readFleet(clients);
  };

  const submit = async (): Promise<void> => {
    setShowConfirm(false);
    const reachable = targetClients.filter(isReachableClient);
    if (view.mode === 'compose') {
      const id = newAnnouncementId();
      const inPickedGroups = new Set(
        groups.filter((g) => pickedGroups.includes(g.groupName)).flatMap((g) => g.clients)
      );
      // The Lambda records the person signed in as the poster.
      const meta = {
        sentTo: describeTargets(
          pickedGroups,
          reachable.length,
          reachable.filter((c) => !inPickedGroups.has(c.name)).length
        ),
      };
      const posted = draft;
      await startRun('post', posted.title, reachable, (c, onUpdate) => postAnnouncement(c, id, posted, meta, onUpdate));
    } else if (view.mode === 'edit') {
      const { id } = view.target;
      const edited = draft;
      await startRun('edit', edited.title, reachable, (c, onUpdate) => editAnnouncement(c, id, edited, onUpdate));
    }
  };

  const runConfirmedAction = async (): Promise<void> => {
    if (!confirmAction) return;
    const { kind, target } = confirmAction;
    setConfirmAction(null);
    const clientsFor = (names: string[]): Client[] =>
      names.map((n) => clientsByName.get(n)).filter((c): c is Client => !!c);
    if (kind === 'revoke') {
      // Only where it is not revoked yet, so a revoke that half failed can be finished.
      // The Lambda records the person signed in as the one who revoked it.
      const names = target.workspaces.filter((w) => w.revokedAt === undefined).map((w) => w.clientName);
      await startRun('revoke', target.title, clientsFor(names), (c, onUpdate) =>
        revokeAnnouncement(c, target.id, target.title, onUpdate)
      );
    } else if (kind === 'restore') {
      await startRun('restore', target.title, clientsFor(target.revokedOn), (c, onUpdate) =>
        restoreAnnouncement(c, target.id, target.title, onUpdate)
      );
    } else {
      // Ends the pop-up now; the history keeps it for the usual 90 days.
      const ended: AnnouncementDraft = { ...target, showUntil: Date.now() };
      const names = target.workspaces.map((w) => w.clientName);
      await startRun('end', target.title, clientsFor(names), (c, onUpdate) =>
        editAnnouncement(c, target.id, ended, onUpdate)
      );
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const now = Date.now();
  const runDone = run
    ? Object.values(run.statuses).filter((s) => s.state === 'success' || s.state === 'failed').length
    : 0;
  const runTotal = run ? Object.keys(run.statuses).length : 0;
  const composing = view.mode === 'compose' || view.mode === 'edit';
  const canReview = draftErrors.length === 0 && targetClients.length > 0 && !run?.running;
  const reachableCount = targetClients.filter(isReachableClient).length;
  const audienceText = draft.audience === 'admins' ? 'Admins only' : 'Everyone';
  const untilText = Number.isFinite(draft.showUntil) ? formatDate(draft.showUntil) : '';

  /** Why the review button is off, or nothing when it is on. */
  const reviewBlocker = (): string | null => {
    if (run?.running) return 'Wait for the send that is running to finish.';
    if (targetClients.length === 0) return 'Pick at least one workspace.';
    if (draftErrors.length > 0) return 'Finish the message first.';
    return null;
  };

  return (
    <ToolPage
      title="Announcements"
      subtitle="Post a message to the people on chosen workspaces. Each person sees it once, as a pop-up, the next time they open Numa."
      actions={
        !composing && (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => void readFleet(clients)}
              disabled={reading || clientsLoading}
            >
              {reading ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <ArrowClockwise className="me-1" aria-hidden="true" />
              )}
              Refresh
            </Button>
            <Button variant="primary" onClick={startCompose} disabled={clientsLoading || run?.running}>
              <Plus className="me-1" aria-hidden="true" />
              New announcement
            </Button>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {run && (
        <Card className="announcement-run">
          <Card.Header className="announcement-run__head">
            <span>
              {run.running ? RUN_VERB[run.kind].doing : RUN_VERB[run.kind].done} &quot;{run.title}&quot;
            </span>
            <span className="announcement-run__count">
              {runDone} of {plural(runTotal, 'workspace')}
            </span>
            {!run.running && (
              <Button size="sm" variant="outline-secondary" onClick={() => setRun(null)}>
                Close
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            <ProgressBar
              now={runTotal === 0 ? 100 : (runDone / runTotal) * 100}
              variant={run.summary && run.summary.failed.length > 0 ? 'warning' : 'success'}
              aria-label={`${runDone} of ${runTotal} workspaces done`}
            />
            {run.summary && (
              <Alert
                variant={run.summary.failed.length === 0 && run.summary.waiting.length === 0 ? 'success' : 'warning'}
                className="announcement-run__summary"
              >
                <div>
                  <strong>{plural(run.summary.succeeded.length, 'workspace')} done</strong>, {run.summary.failed.length}{' '}
                  failed.
                  {run.summary.waiting.length > 0 && (
                    <div>
                      {run.summary.waiting.length} of them cannot show it yet. It is stored, and people see it once{' '}
                      {run.summary.waiting.length === 1 ? 'that workspace is' : 'those workspaces are'} deployed with
                      announcements.
                    </div>
                  )}
                  {run.summary.failed.length > 0 && <div>The reason for each one is in the list below.</div>}
                </div>
                {run.summary.failed.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => void retryFailed()}
                    disabled={run.running}
                  >
                    Try the failed ones again
                  </Button>
                )}
              </Alert>
            )}
          </Card.Body>
          <WorkspaceRunTable statuses={run.statuses} />
        </Card>
      )}

      {composing && (
        <div className="announcement-compose">
          <div className="announcement-compose__main">
            <Card>
              <Card.Header>Workspaces</Card.Header>
              <Card.Body>
                {view.mode === 'edit' ? (
                  <>
                    <p>
                      Changes go to the {plural(view.target.workspaces.length, 'workspace')} that{' '}
                      {view.target.workspaces.length === 1 ? 'holds' : 'hold'} it. People who already closed it do not
                      see it again.
                    </p>
                    <div className="announcement-chips">
                      {view.target.workspaces.map((w) => (
                        <span key={w.clientName} className="announcement-chip mono">
                          {w.clientName}
                        </span>
                      ))}
                    </div>
                  </>
                ) : clientsLoading ? (
                  <div className="announcement-loading">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading clients
                  </div>
                ) : (
                  <>
                    <Form.Group className="mb-3" controlId="announcement-group">
                      <Form.Label>Add a deployment group</Form.Label>
                      <Form.Select value="" onChange={(e) => addGroup(e.target.value)}>
                        <option value="">Choose a group</option>
                        {groups.map((g) => (
                          <option key={g.groupName} value={g.groupName}>
                            {g.groupName} ({g.clients.length})
                          </option>
                        ))}
                      </Form.Select>
                      {pickedGroups.length > 0 && <Form.Text>Added: {pickedGroups.join(', ')}</Form.Text>}
                    </Form.Group>
                    <Form.Label>Or pick workspaces</Form.Label>
                    <GroupedClientSelector
                      clients={selectableClients}
                      selectedClientNames={selectedClientNames}
                      onClientToggle={toggleClient}
                      onSelectClients={selectClients}
                    />
                  </>
                )}
                {unreachable.length > 0 && (
                  <Alert variant="warning" className="announcement-skipped">
                    These have no AWS account on record, so they are skipped:{' '}
                    {unreachable.map((c) => c.name).join(', ')}
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>Message</Card.Header>
              <Card.Body>
                <AnnouncementForm values={values} onChange={setValues} />
              </Card.Body>
            </Card>
          </div>

          <div className="announcement-compose__side">
            <h2 className="section-label">What people see</h2>
            <AnnouncementPreview draft={draft} />
            {draftErrors.length > 0 && (values.title || values.body) && (
              <Alert variant="secondary" className="announcement-todo">
                <strong>Still to fix</strong>
                <ul>
                  {draftErrors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        </div>
      )}

      {composing && (
        <div className="announcement-footer">
          <div className="announcement-footer__summary">
            {reviewBlocker() ?? (
              <>
                <strong>{plural(reachableCount, 'workspace')}</strong>
                <span aria-hidden="true"> · </span>
                {audienceText}
                {untilText && (
                  <>
                    <span aria-hidden="true"> · </span>
                    Pop-up until {untilText}
                  </>
                )}
              </>
            )}
          </div>
          <div className="announcement-footer__actions">
            <Button variant="outline-secondary" onClick={() => setView({ mode: 'list' })}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canReview} onClick={() => setShowConfirm(true)}>
              {view.mode === 'edit' ? 'Review changes and send' : 'Review and send'}
            </Button>
          </div>
        </div>
      )}

      {!composing && (
        <>
          <p className="announcement-list-note">
            {reading
              ? `Reading each workspace: ${readProgress.checked} of ${readProgress.total} checked.`
              : fleet
                ? `${plural(fleet.announcements.length, 'announcement')} across ${plural(fleet.checked, 'workspace')}. The workspaces hold the only copy, so this list is what people see.`
                : 'Reading each workspace.'}
          </p>

          {fleet && fleet.failures.length > 0 && (
            <Alert variant="warning">
              {fleet.failures.length} of {plural(fleet.checked, 'workspace')} could not be read, so{' '}
              {fleet.failures.length === 1 ? 'its announcements are' : 'their announcements are'} missing from this
              list. Press Refresh to try again.
              <details className="announcement-details">
                <summary>Which ones, and why</summary>
                <ul>
                  {fleet.failures.map((f) => (
                    <li key={f.clientName}>
                      <span className="mono">{f.clientName}</span>: {f.error}
                    </li>
                  ))}
                </ul>
              </details>
            </Alert>
          )}

          <Card>
            <Table responsive hover className="mb-0 announcement-table">
              <thead>
                <tr>
                  <th>Announcement</th>
                  <th>Posted</th>
                  <th>Pop-up</th>
                  <th>Who sees it</th>
                  <th>Workspaces</th>
                  <th className="text-end">Seen by</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {!fleet && (reading || clientsLoading) && (
                  <tr>
                    <td colSpan={7} className="announcement-empty">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Reading every workspace
                    </td>
                  </tr>
                )}
                {fleet && fleet.announcements.length === 0 && !reading && (
                  <tr>
                    <td colSpan={7} className="announcement-empty">
                      No workspace has an announcement. Press New announcement to post one.
                    </td>
                  </tr>
                )}
                {fleet?.announcements.map((a) => {
                  const live = a.showUntil > now;
                  const fullyRevoked = a.revokedOn.length > 0 && a.revokedOn.length === a.workspaces.length;
                  const partlyRevoked = a.revokedOn.length > 0 && !fullyRevoked;
                  const canEnd = live && !fullyRevoked;
                  const canRevoke = !fullyRevoked;
                  const canRestore = a.revokedOn.length > 0;
                  return (
                    <tr key={a.id}>
                      <td className="announcement-table__what">
                        <div className="announcement-table__title">{a.title}</div>
                        <div className="announcement-list__body">{a.body}</div>
                      </td>
                      <td>
                        <div>{timeAgo(a.publishedAt)}</div>
                        <div className="announcement-sub">{formatDate(a.publishedAt)}</div>
                        {a.postedBy && <div className="announcement-sub">{a.postedBy}</div>}
                        {a.updatedAt && <div className="announcement-sub">Edited {formatDate(a.updatedAt)}</div>}
                      </td>
                      <td>
                        {fullyRevoked ? (
                          <>
                            <Badge bg="danger">Revoked</Badge>
                            <div className="announcement-sub">
                              {a.revokedAt ? formatDate(a.revokedAt) : ''}
                              {a.revokedBy && <div>{a.revokedBy}</div>}
                            </div>
                          </>
                        ) : (
                          <>
                            <Badge bg={live ? 'success' : 'secondary'}>{live ? 'Showing' : 'Ended'}</Badge>
                            <div className="announcement-sub">
                              {live ? 'until' : 'on'} {formatDate(a.showUntil)}
                            </div>
                            {partlyRevoked && (
                              <div className="announcement-flag">
                                <Badge bg="warning">
                                  Revoked on {a.revokedOn.length} of {a.workspaces.length}
                                </Badge>
                              </div>
                            )}
                            {a.waitingOn.length > 0 && (
                              <div className="announcement-flag">
                                <Badge
                                  bg="warning"
                                  title="It is stored there, but nobody sees it until those workspaces are deployed with announcements."
                                >
                                  Waiting for an update on {a.waitingOn.length} of {a.workspaces.length}
                                </Badge>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td>{a.audience === 'admins' ? 'Admins only' : 'Everyone'}</td>
                      <td>
                        <details className="announcement-details">
                          <summary>{a.sentTo ?? plural(a.workspaces.length, 'workspace')}</summary>
                          <ul>
                            {a.workspaces.map((w) => (
                              <li key={w.clientName}>
                                <span className="mono">{w.clientName}</span>: seen by {w.seenCount}
                                {w.revokedAt !== undefined && ', revoked'}
                                {w.readiness === 'outdated' && ', waiting for an update'}
                              </li>
                            ))}
                          </ul>
                        </details>
                      </td>
                      <td className="text-end numeric">
                        {a.totalSeen.toLocaleString()} {a.totalSeen === 1 ? 'person' : 'people'}
                      </td>
                      <td className="announcement-actions">
                        {!fullyRevoked && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => startEdit(a)}
                            disabled={run?.running}
                          >
                            Edit
                          </Button>
                        )}
                        <Dropdown align="end">
                          <Dropdown.Toggle size="sm" variant="outline-secondary" disabled={run?.running}>
                            More
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {canEnd && (
                              <Dropdown.Item onClick={() => setConfirmAction({ kind: 'end', target: a })}>
                                End the pop-up now
                              </Dropdown.Item>
                            )}
                            {canRevoke && (
                              <Dropdown.Item
                                className="announcement-danger-item"
                                onClick={() => setConfirmAction({ kind: 'revoke', target: a })}
                              >
                                Revoke: hide it from everybody
                              </Dropdown.Item>
                            )}
                            {canRestore && (
                              <Dropdown.Item onClick={() => setConfirmAction({ kind: 'restore', target: a })}>
                                Restore
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{view.mode === 'edit' ? 'Send changes' : 'Send announcement'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="announcement-review">
            <AnnouncementPreview draft={draft} />
            <div>
              <p>
                This goes to <strong>{plural(reachableCount, 'workspace')}</strong>, for{' '}
                {draft.audience === 'admins' ? 'admins only' : 'everyone'}, as a pop-up until{' '}
                <strong>{untilText}</strong>.
              </p>
              <div className="announcement-review__list">
                {targetClients
                  .filter(isReachableClient)
                  .map((c) => c.name)
                  .sort()
                  .map((n) => (
                    <div key={n} className="mono">
                      {n}
                      {readiness?.[n] === 'outdated' && (
                        <Badge bg="warning" className="ms-2">
                          Waiting for an update
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>
              {!readiness && (
                <p className="announcement-review__note">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Checking which workspaces can show it
                </p>
              )}
              {waitingNames.length > 0 && (
                <Alert variant="warning">
                  <strong>{waitingNames.length} of these workspaces cannot show it yet.</strong>{' '}
                  {waitingNames.length === 1 ? 'It runs' : 'They run'} an older version of Numa. The announcement is
                  stored there, and nobody sees it until the workspace is deployed with announcements. If that is after{' '}
                  {untilText || 'the last day'}, there is no pop-up: it is only in the Announcements tab.
                </Alert>
              )}
              {uncheckedNames.length > 0 && (
                <p className="announcement-review__note">
                  {uncheckedNames.join(', ')} could not be checked. The announcement is still sent there.
                </p>
              )}
              <Form.Check
                type="checkbox"
                id="announcement-acknowledge"
                label="I have checked the message and the workspaces."
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirm(false)}>
            Back
          </Button>
          <Button variant="primary" disabled={!acknowledged} onClick={() => void submit()}>
            {view.mode === 'edit'
              ? `Send changes to ${plural(reachableCount, 'workspace')}`
              : `Send to ${plural(reachableCount, 'workspace')}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmAction ? ROW_ACTION[confirmAction.kind].title : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{confirmAction ? ROW_ACTION[confirmAction.kind].body(confirmAction.target) : ''}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmAction?.kind === 'revoke' ? 'danger' : 'primary'}
            onClick={() => void runConfirmedAction()}
          >
            {confirmAction ? ROW_ACTION[confirmAction.kind].confirm : ''}
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
